<template>
  <div class="flex flex-col gap-3">
    <RangeInput
      :current-range="localRange"
      :max-range="maxRange"
      :range-labels="{
        min: $t('Minimum price'),
        max: $t('Maximum price'),
      }"
      :disabled="!canRefine"
      @input="handleRefinement($event)"
    />
    <RangeSlider
      :current-range="localRange"
      :max-range="maxRange"
      :disabled="!canRefine"
      class="px-0.5"
      @input="onSliderInput($event)"
      @end="handleRefinement($event)"
    />
  </div>
</template>

<script lang="ts">


import clamp from 'lodash/clamp.js';
import type { ValueRange, ValueRangeOptional } from '~~/types/ValueRange';
import { EVENT_SOURCE, FILTERS_INTERACTIONS } from '~~/constants/googleTagManager';

export default defineComponent({
  name: 'PricePickerInner',

  props: {
    currentRefinement: {
      type: Object as PropType<ValueRangeOptional>,
      required: true,
    },
    maxRange: {
      type: Object as PropType<ValueRange>,
      required: true,
    },
    canRefine: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['refine'],
  setup(props, { emit }) {

    const { currentRefinement, maxRange } = toRefs(props);
    const localRange = ref(getDefaults());
    const i18n = useI18n();
    const { callClickFilterEvent } = useClickFiltersEvent();

    const localisedName = computed(() => i18n.t('Price'));

    watch([currentRefinement, maxRange], () => {

      localRange.value = getDefaults();
    
    });

    function getDefaults(): ValueRange {

      const { min: rangeMin, max: rangeMax } = maxRange.value;
      const min = clamp(currentRefinement.value.min || rangeMin, rangeMin, rangeMax);
      const max = clamp(currentRefinement.value.max || rangeMax, min, rangeMax);

      return { min, max };
    
    }

    function setValues(range: ValueRange): void {

      localRange.value = range;
    
    }

    function refineRange(range: ValueRange): void {

      emit('refine', range);
    
    }

    function onSliderInput(range: ValueRange): void {

      setValues(range);
    
    }

    function handleRefinement(range: ValueRange): void {

      setValues(range);
      refineRange(range);

      callClickFilterEvent({
        filterInteraction: FILTERS_INTERACTIONS.FILTER_SELECT,
        filterSection: EVENT_SOURCE.PRICE_RANGE,
        filterName: localisedName.value,
        filterValue: { ...localRange.value },
      });
    
    }

    return { localRange, onSliderInput, handleRefinement };
  
  },
});

</script>
